/**
 * Redux Store for Persistent PDF Viewer Zoom State
 * 
 * Features:
 * - Per-drawing zoom persistence (each drawing remembers its zoom)
 * - localStorage persistence (survives browser sessions)
 * - Page number memory (bonus feature)
 * - Global default zoom
 * - Individual and global reset functions
 * 
 * Standards Compliance:
 * - Material Design 3: Preserve user preferences
 * - Adobe Acrobat behavior: Per-document zoom
 * - WCAG 2.1: User control over zoom state
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import { useMemo, useEffect } from 'react';
import { ZOOM_CONFIG } from '../../config/zoom.config.ts';
import type { RootState } from '../types';

/**
 * Zoom state for a single drawing
 */
export interface DrawingZoomState {
  zoom: number;
  page?: number;
  lastModified: number; // timestamp
  rotation?: number; // bonus: remember rotation too
  panOffset?: { x: number; y: number }; // pan position persistence
}

/**
 * Redux slice state
 */
interface ZoomState {
  // State: Map of drawing ID to zoom state
  zoomStates: Record<string, DrawingZoomState>;
  
  // Global default zoom for new drawings
  globalDefault: number;
}

/**
 * Load initial state from localStorage
 */
const loadInitialState = (): ZoomState => {
  try {
    const saved = localStorage.getItem('pdf-viewer-zoom-state');
    if (saved) {
      const parsed = JSON.parse(saved);
      const loadedState = {
        zoomStates: parsed.zoomStates || {},
        globalDefault: parsed.globalDefault ?? ZOOM_CONFIG.DEFAULT_ZOOM,
      };
      console.log('[ZoomStore] Loaded state from localStorage:', {
        drawingCount: Object.keys(loadedState.zoomStates).length,
        globalDefault: loadedState.globalDefault,
      });
      return loadedState;
    } else {
      console.log('[ZoomStore] No saved state found in localStorage, using defaults');
    }
  } catch (error) {
    console.error('[ZoomStore] Failed to load zoom state from localStorage:', error);
    // If localStorage is corrupted, try to clear it
    try {
      localStorage.removeItem('pdf-viewer-zoom-state');
      console.log('[ZoomStore] Cleared corrupted localStorage data');
    } catch (clearError) {
      console.error('[ZoomStore] Failed to clear corrupted localStorage:', clearError);
    }
  }
  return {
    zoomStates: {},
    globalDefault: ZOOM_CONFIG.DEFAULT_ZOOM,
  };
};

/**
 * Save state to localStorage
 */
const saveToLocalStorage = (state: ZoomState) => {
  try {
    const serialized = JSON.stringify(state);
    localStorage.setItem('pdf-viewer-zoom-state', serialized);
    if (process.env.NODE_ENV === 'development') {
      console.log('[ZoomStore] Saved state to localStorage:', {
        drawingCount: Object.keys(state.zoomStates).length,
        globalDefault: state.globalDefault,
      });
    }
  } catch (error) {
    console.error('[ZoomStore] Failed to save zoom state to localStorage:', error);
    // Handle quota exceeded error
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.warn('[ZoomStore] localStorage quota exceeded, clearing old data');
      try {
        // Clear oldest entries if quota is exceeded
        const sortedEntries = Object.entries(state.zoomStates)
          .sort((a, b) => (a[1].lastModified || 0) - (b[1].lastModified || 0));
        // Remove oldest 25% of entries
        const entriesToRemove = Math.floor(sortedEntries.length * 0.25);
        const cleanedState = { ...state };
        sortedEntries.slice(0, entriesToRemove).forEach(([key]) => {
          delete cleanedState.zoomStates[key];
        });
        localStorage.setItem('pdf-viewer-zoom-state', JSON.stringify(cleanedState));
        console.log(`[ZoomStore] Removed ${entriesToRemove} old entries to free space`);
      } catch (cleanupError) {
        console.error('[ZoomStore] Failed to cleanup localStorage:', cleanupError);
      }
    }
  }
};

const initialState: ZoomState = loadInitialState();

/**
 * Redux slice for zoom state management
 */
const zoomSlice = createSlice({
  name: 'zoom',
  initialState,
  reducers: {
    /**
     * Set zoom level for a specific drawing
     */
    setZoom: (state, action: PayloadAction<{ drawingId: string; zoom: number }>) => {
      const { drawingId, zoom } = action.payload;
      state.zoomStates[drawingId] = {
        ...state.zoomStates[drawingId],
        zoom,
        lastModified: Date.now(),
      };
      saveToLocalStorage(state);
    },

    /**
     * Set page number for a specific drawing
     */
    setPage: (state, action: PayloadAction<{ drawingId: string; page: number }>) => {
      const { drawingId, page } = action.payload;
      state.zoomStates[drawingId] = {
        ...state.zoomStates[drawingId],
        page,
        lastModified: Date.now(),
      };
      saveToLocalStorage(state);
    },

    /**
     * Set rotation for a specific drawing
     */
    setRotation: (state, action: PayloadAction<{ drawingId: string; rotation: number }>) => {
      const { drawingId, rotation } = action.payload;
      state.zoomStates[drawingId] = {
        ...state.zoomStates[drawingId],
        rotation,
        lastModified: Date.now(),
      };
      saveToLocalStorage(state);
    },

    /**
     * Set pan offset for a specific drawing
     */
    setPanOffset: (state, action: PayloadAction<{ drawingId: string; panOffset: { x: number; y: number } }>) => {
      const { drawingId, panOffset } = action.payload;
      state.zoomStates[drawingId] = {
        ...state.zoomStates[drawingId],
        panOffset,
        lastModified: Date.now(),
      };
      saveToLocalStorage(state);
    },

    /**
     * Set complete drawing state (zoom, page, rotation, panOffset)
     */
    setDrawingState: (state, action: PayloadAction<{ drawingId: string; state: Partial<DrawingZoomState> }>) => {
      const { drawingId, state: newState } = action.payload;
      state.zoomStates[drawingId] = {
        ...state.zoomStates[drawingId],
        ...newState,
        lastModified: Date.now(),
      };
      saveToLocalStorage(state);
    },

    /**
     * Reset zoom for a specific drawing to global default
     */
    resetZoom: (state, action: PayloadAction<{ drawingId: string }>) => {
      const { drawingId } = action.payload;
      if (state.zoomStates[drawingId]) {
        state.zoomStates[drawingId] = {
          ...state.zoomStates[drawingId],
          zoom: state.globalDefault,
          lastModified: Date.now(),
        };
        saveToLocalStorage(state);
      }
    },

    /**
     * Reset zoom for ALL drawings to global default
     */
    resetAllZoom: (state) => {
      Object.keys(state.zoomStates).forEach((drawingId) => {
        state.zoomStates[drawingId] = {
          ...state.zoomStates[drawingId],
          zoom: state.globalDefault,
          rotation: 0,
          lastModified: Date.now(),
        };
      });
      saveToLocalStorage(state);
    },

    /**
     * Set global default zoom level
     */
    setGlobalDefault: (state, action: PayloadAction<number>) => {
      state.globalDefault = action.payload;
      saveToLocalStorage(state);
    },

    /**
     * Clear state for a specific drawing
     */
    clearDrawingState: (state, action: PayloadAction<{ drawingId: string }>) => {
      const { drawingId } = action.payload;
      delete state.zoomStates[drawingId];
      saveToLocalStorage(state);
    },

    /**
     * Clear all drawing states (factory reset)
     */
    clearAllStates: (state) => {
      state.zoomStates = {};
      state.globalDefault = ZOOM_CONFIG.DEFAULT_ZOOM;
      saveToLocalStorage(state);
    },
  },
});

export const {
  setZoom,
  setPage,
  setRotation,
  setPanOffset,
  setDrawingState,
  resetZoom,
  resetAllZoom,
  setGlobalDefault,
  clearDrawingState,
  clearAllStates,
} = zoomSlice.actions;

export default zoomSlice.reducer;

/**
 * Selectors
 */
export const selectZoomStates = (state: RootState) => state.zoom.zoomStates;
export const selectGlobalDefault = (state: RootState) => state.zoom.globalDefault;
export const selectDrawingState = (drawingId: string) => (state: RootState) => 
  state.zoom.zoomStates[drawingId];
export const selectZoom = (drawingId: string) => (state: RootState) => 
  state.zoom.zoomStates[drawingId]?.zoom ?? state.zoom.globalDefault;
export const selectPage = (drawingId: string) => (state: RootState) => 
  state.zoom.zoomStates[drawingId]?.page;
export const selectRotation = (drawingId: string) => (state: RootState) => 
  state.zoom.zoomStates[drawingId]?.rotation ?? 0;
export const selectPanOffset = (drawingId: string) => (state: RootState) => 
  state.zoom.zoomStates[drawingId]?.panOffset ?? { x: 0, y: 0 };

/**
 * Hook to get zoom state for a specific drawing
 * Convenience hook for components
 */
export function useDrawingZoom(drawingId: string) {
  const dispatch = useDispatch();
  
  // Memoize selector functions to ensure stable references
  const zoomSelector = useMemo(() => selectZoom(drawingId), [drawingId]);
  const pageSelector = useMemo(() => selectPage(drawingId), [drawingId]);
  const rotationSelector = useMemo(() => selectRotation(drawingId), [drawingId]);
  
  // Use memoized selectors - hooks must be called unconditionally
  // Selectors already handle undefined/null cases with fallbacks
  const zoom = useSelector(zoomSelector);
  const page = useSelector(pageSelector);
  const rotation = useSelector(rotationSelector);
  
  // Memoize panOffset selectors to avoid creating new functions on each render
  // Selectors use optional chaining for safe access
  const panOffsetXSelector = useMemo(() => (state: RootState) => 
    state.zoom?.zoomStates?.[drawingId]?.panOffset?.x ?? 0, [drawingId]);
  const panOffsetYSelector = useMemo(() => (state: RootState) => 
    state.zoom?.zoomStates?.[drawingId]?.panOffset?.y ?? 0, [drawingId]);
  
  const panOffsetX = useSelector(panOffsetXSelector);
  const panOffsetY = useSelector(panOffsetYSelector);
  
  // Memoize panOffset object to prevent re-renders
  const panOffset = useMemo(() => ({ x: panOffsetX, y: panOffsetY }), [panOffsetX, panOffsetY]);
  
  // Memoize action creators to prevent re-renders
  const actions = useMemo(() => ({
    setZoom: (newZoom: number) => {
      try {
        dispatch(setZoom({ drawingId, zoom: newZoom }));
        if (process.env.NODE_ENV === 'development') {
          console.log(`[useDrawingZoom] Set zoom for ${drawingId}:`, newZoom);
        }
      } catch (error) {
        console.error('[useDrawingZoom] Error setting zoom:', error);
      }
    },
    setPage: (newPage: number) => {
      try {
        dispatch(setPage({ drawingId, page: newPage }));
        if (process.env.NODE_ENV === 'development') {
          console.log(`[useDrawingZoom] Set page for ${drawingId}:`, newPage);
        }
      } catch (error) {
        console.error('[useDrawingZoom] Error setting page:', error);
      }
    },
    setRotation: (newRotation: number) => {
      try {
        dispatch(setRotation({ drawingId, rotation: newRotation }));
        if (process.env.NODE_ENV === 'development') {
          console.log(`[useDrawingZoom] Set rotation for ${drawingId}:`, newRotation);
        }
      } catch (error) {
        console.error('[useDrawingZoom] Error setting rotation:', error);
      }
    },
    setPanOffset: (newPanOffset: { x: number; y: number }) => {
      try {
        dispatch(setPanOffset({ drawingId, panOffset: newPanOffset }));
      } catch (error) {
        console.error('[useDrawingZoom] Error setting panOffset:', error);
      }
    },
    resetZoom: () => {
      try {
        dispatch(resetZoom({ drawingId }));
        if (process.env.NODE_ENV === 'development') {
          console.log(`[useDrawingZoom] Reset zoom for ${drawingId}`);
        }
      } catch (error) {
        console.error('[useDrawingZoom] Error resetting zoom:', error);
      }
    },
  }), [dispatch, drawingId]);

  // Log initial state in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[useDrawingZoom] Initialized for ${drawingId}:`, {
        zoom,
        page,
        rotation,
        panOffset,
      });
    }
  }, [drawingId]); // Only log when drawingId changes

  return useMemo(() => ({
    zoom,
    page,
    rotation,
    panOffset,
    ...actions,
  }), [zoom, page, rotation, panOffset, actions]);
}

/**
 * Hook for global zoom management
 */
export function useGlobalZoomActions() {
  const dispatch = useDispatch();
  const globalDefault = useSelector(selectGlobalDefault);

  return useMemo(() => ({
    resetAllZoom: () => dispatch(resetAllZoom()),
    setGlobalDefault: (zoom: number) => dispatch(setGlobalDefault(zoom)),
    clearAllStates: () => dispatch(clearAllStates()),
    globalDefault,
  }), [dispatch, globalDefault]);
}